import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { getStock, getStockSummary, restockItem } from '../../services/api';
import {
  ExclamationTriangleIcon, CubeIcon,
  MagnifyingGlassIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STATUS = {
  critical: { label:'Critical',  color:'#dc2626', bg:'#fff1f2', border:'#fecdd3', dot:'#dc2626' },
  low:      { label:'Low Stock', color:'#d97706', bg:'#fef3c7', border:'#fde68a', dot:'#f59e0b' },
  ok:       { label:'In Stock',  color:'#059669', bg:'#ecfdf5', border:'#a7f3d0', dot:'#10b981' },
};

const StockMonitoring = () => {
  const [stock, setStock]             = useState([]);
  const [summary, setSummary]         = useState({ critical:0, low:0, ok:0 });
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filter, setFilter]           = useState('all');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [restocking, setRestocking]   = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [stockRes, summaryRes] = await Promise.all([
        getStock(),
        getStockSummary(),
      ]);
      setStock(stockRes.data.stock || []);
      setSummary(summaryRes.data.summary || { critical:0, low:0, ok:0 });
      setLastRefresh(new Date());
    } catch (err) {
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRestock = async (item) => {
    setRestocking(prev => ({ ...prev, [item.id]: true }));
    try {
      await restockItem(item.id, 10);
      toast.success(`Restocked ${item.product} (+10 units)`);
      loadData();
    } catch {
      toast.error('Restock failed');
    } finally {
      setRestocking(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const filtered = stock.filter(item => {
    const q = search.toLowerCase();
    const matchSearch = item.product?.toLowerCase().includes(q) ||
                        item.fridge?.toLowerCase().includes(q)  ||
                        item.apt?.toLowerCase().includes(q);
    const matchFilter = filter === 'all' || item.status === filter;
    return matchSearch && matchFilter;
  });

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const diff = (new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 3;
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }

    .sm-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:2rem; flex-wrap:wrap; gap:1rem; }
    .sm-title   { font-size:1.6rem; font-weight:900; color:#111827; }
    .sm-sub     { font-size:0.88rem; color:#64748b; font-weight:600; margin-top:0.15rem; }

    .sm-refresh-btn {
      display:flex; align-items:center; gap:0.4rem;
      padding:0.5rem 1.1rem; border-radius:9999px;
      background:#eff6ff; color:#2563eb; border:1.5px solid #bfdbfe;
      font-weight:700; font-size:0.82rem; cursor:pointer; font-family:'Nunito',sans-serif; transition:all 0.2s;
    }
    .sm-refresh-btn svg { width:0.9rem; height:0.9rem; }
    .sm-refresh-btn:hover { background:#2563eb; color:white; border-color:#2563eb; }

    .sm-summary { display:grid; grid-template-columns:repeat(3,1fr); gap:1.25rem; margin-bottom:2rem; }
    .sm-sum-card {
      background:white; border-radius:1.5rem; padding:1.25rem 1.5rem;
      border:1.5px solid; display:flex; align-items:center; gap:1rem;
      box-shadow:0 4px 12px rgba(0,0,0,0.04); transition:all 0.3s; cursor:pointer;
    }
    .sm-sum-card:hover { transform:translateY(-3px); box-shadow:0 12px 24px rgba(0,0,0,0.08); }
    .sm-sum-dot   { width:14px; height:14px; border-radius:50%; flex-shrink:0; }
    .sm-sum-count { font-size:1.75rem; font-weight:900; line-height:1; }
    .sm-sum-label { font-size:0.8rem; font-weight:700; color:#64748b; margin-top:0.15rem; }

    .sm-toolbar { display:flex; align-items:center; gap:0.75rem; margin-bottom:1.5rem; flex-wrap:wrap; }
    .sm-search-wrap { position:relative; flex:1; min-width:200px; }
    .sm-search-icon { position:absolute; left:0.9rem; top:50%; transform:translateY(-50%); width:1rem; height:1rem; color:#9ca3af; pointer-events:none; }
    .sm-search {
      width:100%; padding:0.65rem 1rem 0.65rem 2.5rem;
      border:1.5px solid #e5e7eb; border-radius:9999px;
      font-size:0.88rem; font-family:'Nunito',sans-serif; font-weight:600; color:#111827;
      background:#f9fafb; outline:none; transition:all 0.2s;
    }
    .sm-search:focus { border-color:#059669; background:white; box-shadow:0 0 0 3px rgba(5,150,105,0.1); }

    .sm-filter-group { display:flex; background:#f1f5f9; border-radius:9999px; padding:0.2rem; }
    .sm-filter-btn {
      padding:0.38rem 0.85rem; border-radius:9999px; border:none; cursor:pointer;
      font-weight:700; font-size:0.78rem; color:#64748b; background:none;
      font-family:'Nunito',sans-serif; transition:all 0.2s;
    }
    .sm-filter-btn.active { background:white; color:#374151; box-shadow:0 2px 8px rgba(0,0,0,0.08); }

    .sm-table-wrap { background:white; border-radius:1.5rem; overflow:hidden; border:1px solid #f1f5f9; box-shadow:0 4px 16px rgba(0,0,0,0.04); }
    .sm-table { width:100%; border-collapse:collapse; }
    .sm-th { padding:0.9rem 1.25rem; text-align:left; font-size:0.72rem; font-weight:800; color:#64748b; text-transform:uppercase; letter-spacing:0.6px; background:#f8fafc; border-bottom:1px solid #f1f5f9; white-space:nowrap; }
    .sm-td { padding:1rem 1.25rem; border-bottom:1px solid #f8fafc; font-size:0.87rem; color:#374151; vertical-align:middle; }
    .sm-tr:last-child .sm-td { border-bottom:none; }
    .sm-tr:hover .sm-td { background:#fafbfc; }

    .sm-fridge-name  { font-weight:800; color:#111827; font-size:0.9rem; }
    .sm-apt-name     { font-size:0.75rem; color:#94a3b8; font-weight:600; margin-top:0.1rem; }
    .sm-product-name { font-weight:700; color:#111827; }
    .sm-price        { font-weight:800; color:#059669; }
    .sm-expiry       { font-size:0.82rem; font-weight:600; }
    .sm-expiry.soon  { color:#d97706; }
    .sm-expiry.ok    { color:#64748b; }

    .sm-qty-wrap  { display:flex; align-items:center; gap:0.5rem; }
    .sm-qty-num   { font-weight:900; font-size:1rem; }
    .sm-qty-bar-wrap { width:50px; height:5px; background:#f1f5f9; border-radius:9999px; overflow:hidden; }
    .sm-qty-bar   { height:100%; border-radius:9999px; }

    .sm-status-badge { display:inline-flex; align-items:center; gap:0.35rem; font-size:0.72rem; font-weight:700; padding:0.25rem 0.65rem; border-radius:9999px; border:1.5px solid; white-space:nowrap; }
    .sm-status-dot   { width:6px; height:6px; border-radius:50%; flex-shrink:0; }

    .sm-restock-btn {
      padding:0.4rem 0.85rem; border-radius:9999px;
      background:#ecfdf5; color:#059669; border:1.5px solid #a7f3d0;
      font-weight:700; font-size:0.75rem; cursor:pointer; font-family:'Nunito',sans-serif; transition:all 0.2s; white-space:nowrap;
    }
    .sm-restock-btn:hover   { background:#059669; color:white; border-color:#059669; }
    .sm-restock-btn:disabled { opacity:0.5; cursor:not-allowed; }

    .sm-empty { padding:4rem 2rem; text-align:center; color:#94a3b8; }
    .sm-empty svg { width:3rem; height:3rem; margin:0 auto 0.75rem; opacity:0.35; }
    .sm-refresh-info { font-size:0.75rem; color:#9ca3af; font-weight:600; text-align:right; margin-bottom:0.75rem; }

    /* Spinner */
    .sm-spinner { width:2.5rem; height:2.5rem; border:3px solid #f3f4f6; border-top-color:#059669; border-radius:50%; animation:spin 0.7s linear infinite; margin:4rem auto; }
    @keyframes spin { to{ transform:rotate(360deg); } }
  `;

  if (loading) return (
    <AdminLayout>
      <style>{styles}</style>
      <div className="sm-spinner" />
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <style>{styles}</style>

      <div className="sm-header">
        <div>
          <div className="sm-title">📦 Stock Monitoring</div>
          <div className="sm-sub">Real-time inventory across all fridges</div>
        </div>
        <button className="sm-refresh-btn" onClick={loadData}>
          <ArrowPathIcon /> Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="sm-summary">
        {[
          { key:'critical', label:'Critical'  },
          { key:'low',      label:'Low Stock' },
          { key:'ok',       label:'In Stock'  },
        ].map(s => {
          const st = STATUS[s.key];
          return (
            <div key={s.key}
              className="sm-sum-card"
              style={{
                borderColor: st.border,
                background:  filter === s.key ? st.bg : 'white',
                boxShadow:   filter === s.key ? `0 0 0 3px ${st.border}` : undefined,
              }}
              onClick={() => setFilter(filter === s.key ? 'all' : s.key)}>
              <div className="sm-sum-dot" style={{ background: st.dot }} />
              <div>
                <div className="sm-sum-count" style={{ color: st.color }}>{summary[s.key]}</div>
                <div className="sm-sum-label">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="sm-toolbar">
        <div className="sm-search-wrap">
          <MagnifyingGlassIcon className="sm-search-icon" />
          <input className="sm-search"
            placeholder="Search by product, fridge or apartment…"
            value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="sm-filter-group">
          {['all','critical','low','ok'].map(f => (
            <button key={f}
              className={`sm-filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : STATUS[f].label}
            </button>
          ))}
        </div>
      </div>

      <div className="sm-refresh-info">Last updated: {lastRefresh.toLocaleTimeString()}</div>

      {/* Table */}
      <div className="sm-table-wrap">
        {filtered.length === 0 ? (
          <div className="sm-empty">
            <CubeIcon />
            <p style={{ fontWeight: 700 }}>
              {stock.length === 0 ? 'No stock data found — add products to fridges first' : 'No items match your filters'}
            </p>
          </div>
        ) : (
          <table className="sm-table">
            <thead>
              <tr>
                {['Fridge','Product','Price','Qty / Min','Expiry','Status','Action'].map(h => (
                  <th key={h} className="sm-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => {
                const st     = STATUS[item.status] || STATUS.ok;
                const pct    = Math.min(100, Math.round((item.qty / ((item.min || 5) * 2)) * 100));
                const expSoon = isExpiringSoon(item.expiry);
                return (
                  <tr key={item.id} className="sm-tr">
                    <td className="sm-td">
                      <div className="sm-fridge-name">{item.fridge}</div>
                      <div className="sm-apt-name">{item.apt}</div>
                    </td>
                    <td className="sm-td">
                      <span className="sm-product-name">{item.product}</span>
                    </td>
                    <td className="sm-td">
                      <span className="sm-price">₹{item.price}</span>
                    </td>
                    <td className="sm-td">
                      <div className="sm-qty-wrap">
                        <span className="sm-qty-num" style={{ color: st.color }}>{item.qty}</span>
                        <span style={{ color:'#94a3b8', fontSize:'0.78rem', fontWeight:600 }}>/{item.min}</span>
                        <div className="sm-qty-bar-wrap">
                          <div className="sm-qty-bar" style={{ width:`${pct}%`, background:st.dot }} />
                        </div>
                      </div>
                    </td>
                    <td className="sm-td">
                      <span className={`sm-expiry ${expSoon ? 'soon' : 'ok'}`}>
                        {expSoon ? '⚠ ' : ''}{item.expiry ? new Date(item.expiry).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="sm-td">
                      <span className="sm-status-badge"
                        style={{ color:st.color, background:st.bg, borderColor:st.border }}>
                        <span className="sm-status-dot" style={{ background:st.dot }} />
                        {st.label}
                      </span>
                    </td>
                    <td className="sm-td">
                      {item.status !== 'ok' && (
                        <button className="sm-restock-btn"
                          disabled={restocking[item.id]}
                          onClick={() => handleRestock(item)}>
                          {restocking[item.id] ? '⏳ ...' : '+ Restock'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
};

export default StockMonitoring;